import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import color from 'color';
import moment from 'moment';
import classNames from 'class-names';
import { Button, Container, Row } from 'react-bootstrap';
import { CreditCard, Clock, XCircle, User } from 'react-feather';
import Select, { components } from 'react-select';
import Loader from 'react-loader-spinner';
import { Redirect } from 'react-router-dom';

import { useDialog } from '../../../../hooks/useDialog';

import ScreenHeader from '../../../../components/ScreenHeader/ScreenHeader';
import ScreenFooter from '../../../../components/ScreenFooter/ScreenFooter';
import { AuthForm } from '../../../../components/AuthForm';
import Col from '../../../../components/Col/Col';
import SignInContextProvider from '../../../../components/SignInContextProvider/SignInContextProvider';
import { OpenNewWindowConfirmDialog } from '../../../../components/Common/openNewWindowConfirmDialog';

import Styles from './Confirm.module.scss';
import { formatPrice } from "../../../../helpers/function/format";

import * as actions from '../../../../store/actions';

import { ROUTER_URL } from '../../../../const/ROUTER_URL';
import { PAYMENT_TYPE } from '../../../../const/payment/PAYMENT_TYPE';
import { useFormatedText } from '../../../../hooks/useFormattedText';

const MenuListWrapper = ({ children, innerProps, ...props }) => (
    <components.MenuList {...props} innerProps={{ ...innerProps, 'data-testid': 'data-test-bankSelect-menu' }}>
        {children}
    </components.MenuList>
);

const OptionWrapper = ({ children, innerProps, ...props }) => (
    <components.Option {...props} innerProps={{ ...innerProps, 'data-testid': `data-test-bankSelect-option-${children}` }}>
        {children}
    </components.Option>
);

const SelectContainerWrapper = ({ children, innerProps, ...props }) => (
    <components.SelectContainer {...props} innerProps={{ ...innerProps, 'data-testid': 'data-test-bankSelect-container' }}>
        {children}
    </components.SelectContainer>
);

const Confirm = () => {
    const { t } = useTranslation('common');
    const { bookType } = useParams();
    const { timestamp } = useLocation().state || {};
    const { productID } = useLocation().state || {};
    const history = useHistory();

    const shopId = useSelector(state => state.companies.shopId);
    const tintColor = useSelector(state => state.companies.tintColor);
    const { company } = useSelector(state => state.companies.selectedCompany) ?? {};
    const {
        id,
        roomID,
        serviceID,
        name,
        description,
        minutes,
        time,
        price,
        length,
        isHidePrice,
    } = useSelector(state => {
        switch (bookType) {
            case 'service':
                let booking = {};
                if (state.booking.serviceCategories && state.booking.selectedServiceID) {
                    Object.values(state.booking.serviceCategories).some((category) => {
                        return category.some((service) => {
                            if (service.sub_options.length) {
                                return service.sub_options.some((subService) => {
                                    const isSelected = subService.id === state.booking.selectedServiceID;
                                    if (isSelected) {
                                        booking = {
                                            ...subService,
                                            hide_price: service.hide_price,
                                        };
                                    }
                                    return isSelected;
                                })
                            } else {
                                const isSelected = service.id === state.booking.selectedServiceID;
                                if (isSelected) {
                                    booking = service;
                                }
                                return isSelected;
                            }
                        })
                    })
                }

                return {
                    id: booking.id,
                    roomID: productID,
                    serviceID: booking.id,
                    minutes: booking.minutes,
                    name: booking.name,
                    description: booking.description,
                    time: booking.time,
                    price: booking.price,
                    length: booking.length,
                    isHidePrice: booking.hide_price,
                };
            case 'group':
                let group = {};
                if (state.groups.groups && state.groups.selectedGroupID) {
                    state.groups.groups.some(({ groups: dayGroups }) => {
                        return dayGroups.some((dayGroup) => {
                            if (dayGroup.serviceSubOptions.length) {
                                return dayGroup.serviceSubOptions.some((subGroup) => {
                                    const isSelected = `${subGroup.id}_${subGroup.serviceId}` === state.groups.selectedGroupID;
                                    if (isSelected) {
                                        group = {
                                            ...subGroup,
                                            description: dayGroup.description,
                                            hide_price: dayGroup.hide_price,
                                        };
                                    }
                                    return isSelected;
                                })
                            } else {
                                const isSelected = `${dayGroup.id}_${dayGroup.serviceId}` === state.groups.selectedGroupID;
                                if (isSelected) {
                                    group = dayGroup;
                                }
                                return isSelected;
                            }
                        })
                    })
                }

                return {
                    id: group.id,
                    roomID: group.roomId,
                    serviceID: group.serviceId,
                    name: group.serviceName,
                    description: group.description,
                    time: group.time,
                    price: parseFloat(group.cost),
                    length: group.length,
                    isHidePrice: group.hide_price,
                };
            case 'bundle':
                const bundle = Boolean(state.bundles.bundles && state.bundles.selectedBundleID)
                    ? state.bundles.bundles.find(({ id }) => id === state.bundles.selectedBundleID)
                    : {};

                return {
                    id: bundle.id,
                    name: bundle.name,
                    description: bundle.description,
                    price: bundle.price,
                    length: bundle.length,
                    isHidePrice: bundle.hide_price,
                };
            default:
                let selectedEvent = {};
                if (state.events.events && state.events.selectedEventID) {
                    state.events.events.some((event) => {
                        if (event.serviceSubOptions.length) {
                            return event.serviceSubOptions.some((subOption) => {
                                const isSelected = (
                                    String(subOption.id) === String(state.events.selectedEventID)
                                    || String(subOption.serviceId) === String(state.events.selectedEventID)
                                );

                                if (isSelected) {
                                    selectedEvent = {
                                        ...subOption,
                                        photo_url: event.photo_url,
                                        contact_number: event.contact_number,
                                        street: event.street,
                                        city: event.city,
                                        zip: event.zip,
                                        country: event.country,
                                        latitude: event.latitude,
                                        longitude: event.longitude,
                                        hide_price: event.hide_price,
                                    };
                                }

                                return isSelected;
                            })
                        } else {
                            const isSelected = (
                                String(event.id) === String(state.events.selectedEventID)
                                || String(event.serviceId) === String(state.events.selectedEventID)
                            );
                            if (isSelected) {
                                selectedEvent = event;
                            }
                            return isSelected;
                        }
                    })
                }

                return {
                    id: selectedEvent.id,
                    roomID: selectedEvent.roomId,
                    serviceID: selectedEvent.serviceId,
                    name: selectedEvent.serviceName ?? selectedEvent.groupName, // fallback to groupName should not be required, but we keep it for now
                    time: selectedEvent.time,
                    price: parseFloat(selectedEvent.cost),
                    length: selectedEvent.length,
                    isHidePrice: selectedEvent.hide_price,
                };
        }
    });

    const style = useSelector(state => state.widget.style);
    const isLoggedIn = useSelector(state => state.auth.isSignup);
    const {
        show_discount: showDiscount,
        discount_string: discountMessage,
        payments: paymentOptions,
        ideal_banks: banks,
        booking_cost: bookingCost,
        pay_online: payOnline,
        final_cost: finalCost
    } = useSelector(state => (bookType === 'bundle' ? state.bundles.paymentOptions : state.payment.paymentOptions) || {});
    const { name: productName, photo_url: productPhoto } = useSelector(state => state.booking.selectedProduct || {});
    const paymentOptionsError = useSelector(state => bookType !== 'bundle' ? state.payment.paymentOptionsError : null);
    const confirmPaymentProgres = useSelector(state => bookType !== 'bundle' ? state.payment.confirmPaymentProgres : null);
    const step = useSelector(state => state.auth.step);
    const bookingID = useSelector(state => bookType !== 'bundle' ? state.payment.bookingID : state.bundles.clientBundleID);
    const url = useSelector(state => bookType !== 'bundle' ? state.payment.paymentURL : state.bundles.paymentURL);
    const dispatch = useDispatch();

    const [paymentOption, setPaymentOption] = useState(null);
    const [bankID, setBankID] = useState(null);

    const isShowPrice = (
        !(isHidePrice === true && Number(price) === 0))
        || paymentOptions
            ?.every(({ payment_type }) => ['bundle', 'subscription'].includes(payment_type))
        ;

    const openNewWindowConfirmDialog = useDialog(false)

    let signInLabel;
    switch (step) {
        case 'login':
            signInLabel = 'bookingConfirmScreen.signInToYourAccount';
            break;
        case 'verifyPassword':
            signInLabel = 'bookingConfirmScreen.youAreAlreadyRegistered';
            break;
        case 'verifyEmail':
            signInLabel = 'bookingConfirmScreen.enterCode';
            break;
        case 'fillProfile':
            signInLabel = 'bookingConfirmScreen.signUp';
            break;
        default:
            signInLabel = 'bookingConfirmScreen.whatIsYourEmailAddress';
            break;
    }

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        if (bookType === 'bundle') {
            dispatch(actions.getBundlePaymentOptions(id));
        } else {
            dispatch(actions.getPaymentOptions(roomID, serviceID, timestamp || time))
        }
    }, [isLoggedIn, id, roomID, serviceID, time, timestamp, bookType, dispatch]);

    useEffect(() => {
        if (!paymentOptions || paymentOptions.length > 1 || paymentOption) {
            return;
        }

        setPaymentOption(paymentOptions[0].payment_type);
    }, [paymentOptions, paymentOption]);

    useEffect(() => {
        return () => {
            dispatch(actions.getPaymentOptionsSucceeded(null));
        }
    }, [dispatch])

    const currentPaymentOption = paymentOptions?.find(({ payment_type: type }) => type === paymentOption) || {};
    const showBanks = currentPaymentOption['requires_ideal_bank'];
    const onlinePayment = currentPaymentOption['online_payment'];

    useEffect(() => {
        if (!paymentOption || (paymentOption === PAYMENT_TYPE.IDEAL && !bankID && !onlinePayment)) {
            return;
        }

        if (bookType === 'bundle') {
            dispatch(actions.buyBundle(id, paymentOption, bankID, bookingID));
        }
        // eslint-disable-next-line
    }, [id, bookType, roomID, serviceID, timestamp, time, paymentOption, bankID, onlinePayment, dispatch]);


    const isDisabledButton = (!bookingID && bookType === 'bundle' && !onlinePayment)
        || (bookType !== 'bundle' && confirmPaymentProgres)
        || (!bankID && showBanks)

    const handleSubmitButton = useCallback(() => {
        if (isDisabledButton) {
            return;
        }

        if (url) {
            window.open(url, '_blank');

            if (bookType !== 'bundle') {
                history.push(ROUTER_URL.BOOK_SUCCESS_PAYMENT);
            }
        }

        if (bookType === 'bundle') {
            dispatch(actions.confirmBundlePayment({
                paymentType: paymentOption,
                onlinePayment,
                history,
                url: ROUTER_URL.BOOK_SUCCESS_BUNDLE,
            }));
        } else {
            dispatch(actions.confirmPayment(
                {
                    paymentType: paymentOption,
                    onlinePayment,
                    roomId: roomID,
                    serviceId: serviceID,
                    timestamp: timestamp || time,
                    idealBankId: bankID,
                    history,
                    url: ROUTER_URL.BOOK_SUCCESS_PAYMENT,
                }
            ));
        }
    }, [
        onlinePayment,
        isDisabledButton,
        url,
        bookType,
        paymentOption,
        history,
        roomID,
        serviceID,
        timestamp,
        time,
        bankID,
        dispatch
    ])

    useEffect(() => {
        if (url && bookType !== 'bundle') {
            openNewWindowConfirmDialog.onShow()
        } else {
            openNewWindowConfirmDialog.onClose()
        }
        // eslint-disable-next-line
    }, [url])

    const openNewWindowConfirm = useCallback(() => {
        window.open(url, '_blank');
        history.push(ROUTER_URL.BOOK_SUCCESS_PAYMENT);
    }, [url, history])

    const preparedDescription = useFormatedText(description);

    if (!id) {
        return <Redirect to={ROUTER_URL.SHOPS_ID({ shopId })} />
    }

    const selectedBank = banks && banks.find(({ id }) => id === bankID);

    if (paymentOptionsError) {
        return (
            <>
                <ScreenHeader>
                    <h2 className={Styles.title} data-testid='data-test-confirmAVisit-title'>
                        {t('bookingScreen.confirmAVisit')}
                    </h2>
                </ScreenHeader>

                <div className={classNames(
                    Styles.container,
                    style !== 'side' ? 'pt-md-4' : undefined,
                    'pb-4'
                )}>
                    <Container className={classNames(
                        style !== 'side' && 'px-md-0',
                        'd-flex',
                        'flex-column'
                    )}>
                        <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                            <XCircle size={64} color={'red'} />
                            <p className={Styles.error}>
                                {paymentOptionsError}
                            </p>
                        </div>
                    </Container>
                </div>
            </>
        )
    }

    return (
        <SignInContextProvider>
            <ScreenHeader>
                <h2 className={Styles.title} data-testid='data-test-confirmAVisit-title'>
                    {t('bookingScreen.confirmAVisit')}
                </h2>
            </ScreenHeader>

            <div
                className={classNames(
                    Styles.container,
                    style !== 'side' ? 'pt-md-4' : undefined,
                )}
            >
                <Container
                    className={classNames(
                        style !== 'side' && 'px-md-0',
                        'd-flex',
                        'flex-column'
                    )}
                    style={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingBottom: (style === 'dev' || style === 'website')
                            && (!isLoggedIn || (paymentOption && (!showBanks || (showBanks && bankID))))
                            ? '210px'
                            : '30px'
                    }}
                >
                    <Row>
                        <Col md={6} className={`d-flex flex-column mt-4`}>
                            <span className={Styles.label} data-testid='data-test-dateAndService-label'>
                                {t('bookingConfirmScreen.dateAndService')}
                            </span>

                            {(bookType === 'group' || bookType === 'event') && (
                                <>
                                    <span className={Styles.date} data-testid='data-test-date'>
                                        {moment.unix((time)).utc().format('ddd D MMMM YYYY')}
                                    </span>
                                    <span className={Styles.length} data-testid='data-test-length'>
                                        {length}
                                    </span>
                                </>
                            )}
                            {bookType === 'service' && (
                                <>
                                    <span className={Styles.date} data-testid='data-test-date'>
                                        {moment.unix((time || timestamp)).utc().format('ddd D MMMM YYYY')}
                                    </span>
                                    <span className={Styles.date} data-testid='data-test-time'>
                                        {`${moment.unix((time || timestamp)).utc().format('HH:mm')} - ${moment.unix(((time || timestamp) + minutes * 60)).utc().format('HH:mm')} `}
                                    </span>
                                </>
                            )}

                            <span className={Styles.name} data-testid={`data-test-service-button-${name}`}>
                                {name}
                            </span>


                            <div className={Styles.details}>
                                {isShowPrice && (
                                    <div className={Styles.detail}>
                                        <div>
                                            <CreditCard size={18} />
                                        </div>
                                        <span className={Styles.value} data-testid='data-test-price'>
                                            {formatPrice(price)}
                                        </span>
                                    </div>
                                )}

                                <div className={Styles.detail}>
                                    <div>
                                        <Clock size={18} />
                                    </div>
                                    <span className={Styles.value} data-testid='data-test-length'>
                                        {length}
                                    </span>
                                </div>
                            </div>

                            {preparedDescription && (
                                <div>
                                    <span className={classNames(Styles.label, 'mt-4 mb-2')}>
                                        {t('bookingConfirmScreen.description')}
                                    </span>
                                    <span data-testid='data-test-description' className={Styles.description}>
                                        {preparedDescription}
                                    </span>
                                </div>
                            )}

                            {company?.name && (
                                <div>
                                    <span className={classNames(Styles.label, 'mt-4 mb-2')}>
                                        {t('bookingConfirmScreen.companyName')}
                                    </span>
                                    <span data-testid='data-test-company-name' className={Styles.description}>
                                        {company.name}
                                    </span>
                                </div>
                            )}

                            {company?.coc_number && (
                                <div>
                                    <span className={classNames(Styles.label, 'mt-4 mb-2')}>
                                        {t('bookingConfirmScreen.cocNumber')}
                                    </span>
                                    <span data-testid='data-test-coc-number' className={Styles.description}>
                                        {company.coc_number}
                                    </span>
                                </div>
                            )}

                            {bookType === 'service' && roomID !== 0 && (
                                <div className={Styles.product}>
                                    {productPhoto ? <img
                                        className={Styles.productPhoto}
                                        src={productPhoto}
                                        alt={productName}
                                    /> : (
                                        <div className={Styles.productPhoto}>
                                            <User className={Styles.icon} size={24} />
                                        </div>
                                    )}


                                    <span className={Styles.productName} data-testid='data-test-productName'>
                                        {productName}
                                    </span>
                                </div>
                            )}
                        </Col>

                        <Col md={6} className={'mt-4'}>
                            {!isLoggedIn && (
                                <>
                                    <span className={Styles.label} data-testid='data-test-signIn-label'>
                                        {t(signInLabel)}
                                    </span>

                                    <AuthForm showFooter={false} />
                                </>
                            )}

                            {isLoggedIn && isShowPrice && (
                                <>
                                    <span className={Styles.label} data-testid='data-test-paymentMethod-label'>
                                        {t('bookingConfirmScreen.paymentMethod')}
                                    </span>

                                    {!paymentOptions && (
                                        <div className="d-flex justify-content-center">
                                            <Loader
                                                type="TailSpin"
                                                color="#000"
                                                height={28}
                                                width={28}
                                            />
                                        </div>
                                    )}

                                    {paymentOptions && (
                                        <div className={Styles.paymentOptions}>
                                            {paymentOptions.map(({ payment_type: type, payment_name: name }) => (
                                                <button
                                                    data-testid={`data-test-paymentOption-${type}`}
                                                    key={type}
                                                    className={classNames(
                                                        Styles.paymentOption,
                                                        paymentOption === type ? Styles.selected : undefined
                                                    )}
                                                    onClick={() => setPaymentOption(type)}
                                                >
                                                    {name}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {showBanks && (
                                        <Select
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    borderRadius: 0,
                                                    border: 0,
                                                    borderBottom: '1px solid #D8E1EA',
                                                    boxShadow: 'none'
                                                }),
                                                indicatorSeparator: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: 'transparent'
                                                }),
                                            }}
                                            theme={theme => ({
                                                ...theme,
                                                colors: {
                                                    ...theme.colors,
                                                    primary: tintColor,
                                                    primary75: color(tintColor).lighten(0.25).string(),
                                                    primary50: color(tintColor).lighten(0.5).string(),
                                                    primary25: color(tintColor).lighten(0.75).string()
                                                }
                                            })}
                                            placeholder={t('bookingConfirmScreen.selectBank')}
                                            options={banks.map(({ name: label, id: value }) => ({ value, label }))}
                                            value={selectedBank && {
                                                value: selectedBank.id,
                                                label: selectedBank.name
                                            }}
                                            components={{
                                                MenuList: MenuListWrapper,
                                                Option: OptionWrapper,
                                                SelectContainer: SelectContainerWrapper
                                            }}
                                            onChange={({ value }) => setBankID(value)}
                                            isSearchable={false}
                                            data-testid='data-test-bankSelect'
                                        />
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>

                    {paymentOption && (
                        <div className={Styles.pricing}>
                            {showDiscount && isShowPrice && (
                                <>
                                    <span className={Styles.label}>{t('bookingConfirmScreen.discount')}</span>
                                    <span className={Styles.discount} data-testid='data-test-discount-message'>
                                        {discountMessage}
                                    </span>
                                </>
                            )}

                            {paymentOption !== PAYMENT_TYPE.CASH && parseFloat(bookingCost) !== 0 && isShowPrice && (
                                <div className={'mb-4'}>
                                    <span className={Styles.label}>{t('bookingConfirmScreen.bookingCost')}</span>
                                    <span
                                        className={Styles.price}
                                        data-testid='data-test-bookingCost'>
                                        {formatPrice(bookingCost)}
                                    </span>
                                </div>
                            )}

                            {(paymentOption === PAYMENT_TYPE.IDEAL || (onlinePayment && paymentOption !== PAYMENT_TYPE.CASH)) && isShowPrice && (
                                <div className={'mb-4'}>
                                    <span className={Styles.label}>{t('bookingConfirmScreen.leftToPay')}</span>
                                    <span
                                        className={Styles.price}
                                        data-testid='data-test-leftToPay'>
                                        {formatPrice(payOnline)}
                                    </span>
                                </div>
                            )}

                            {paymentOption === PAYMENT_TYPE.CASH && isShowPrice && (
                                <div className={'mb-4'}>
                                    <span className={Styles.label}>{t('bookingConfirmScreen.total')}</span>
                                    <span
                                        className={Styles.price}
                                        data-testid='data-test-total'>
                                        {formatPrice(finalCost)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </Container>
            </div>

            <ScreenFooter visible={!isLoggedIn || (paymentOption && (!showBanks || (showBanks && bankID)))}>
                {!isLoggedIn && <AuthForm.Footer />}

                {paymentOption && (!showBanks || (showBanks && bankID)) && (
                    <>
                        <Button
                            variant={'tint'}
                            size={'lg'}
                            className={Styles.confirmButton}
                            onClick={handleSubmitButton}
                            disabled={isDisabledButton}
                            data-testid='data-test-confirmButton'
                        >
                            {isDisabledButton ? (
                                <Loader
                                    className="d-block text-center"
                                    type="TailSpin"
                                    color="#fff"
                                    height={24}
                                    width={24}
                                />
                            ) : t('bookingConfirmScreen.continue')}
                        </Button>

                        <OpenNewWindowConfirmDialog
                            isVisible={openNewWindowConfirmDialog.visible}
                            onConfirm={openNewWindowConfirm}
                            onClose={openNewWindowConfirmDialog.onClose}
                        />
                    </>
                )}
            </ScreenFooter>
        </SignInContextProvider>
    )
};

export default Confirm
